import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Circle, MailCheck, Globe, ShieldCheck, ArrowLeft, ArrowRight, Inbox,
} from "lucide-react";

export default function VerifyCampaign() {
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [otp, setOtp] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [checkingSite, setCheckingSite] = useState(false);
  const [editingSite, setEditingSite] = useState(false);
  const [siteInput, setSiteInput] = useState("");
  const [savingSite, setSavingSite] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);

  useEffect(() => {
    api.get("/political-os/my-entities")
      .then(({ data }) => {
        const list = data?.entities || [];
        const active = list.find(e => e.id === data?.active_entity_id) || list[0] || null;
        if (!active) { navigate("/onboarding/political"); return; }
        setEntity(active);
        setSummary(active.verification_summary || null);
        setReviewRequested(Boolean(active.verification?.manual_review_requested));
      })
      .catch(e => toast.error(errMsg(e, "Could not load your campaign")))
      .finally(() => setLoading(false));
  }, [navigate]);

  const refreshSummary = async () => {
    try {
      const { data } = await api.get(`/political-os/entities/${entity.id}/verification`);
      setSummary(data);
    } catch { /* keep old */ }
  };

  const sendOtp = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/verify-email/send`);
      setOtpSent(true);
      toast.success(`Verification code sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e, "Could not send verification code")); }
    finally { setSending(false); }
  };

  const confirmOtp = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/verify-email/confirm`, { code: otp.trim() });
      setSummary(data.verification_summary);
      toast.success("Official email verified");
      if (entity.website) {
        api.post(`/political-os/entities/${entity.id}/verify-website`)
          .then(({ data: d }) => setSummary(d.verification_summary))
          .catch(() => {});
      }
    } catch (e) { toast.error(errMsg(e, "Verification failed")); }
    finally { setConfirming(false); }
  };

  const checkWebsite = async () => {
    setCheckingSite(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/verify-website`);
      setSummary(data.verification_summary);
      toast.success(data.checks?.email_found || data.checks?.name_found
        ? "Website verified — your campaign name/email was found on the site"
        : "Website reached, but we couldn't find your campaign name or email on it");
    } catch (e) { toast.error(errMsg(e, "Website check failed")); }
    finally { setCheckingSite(false); }
  };

  const saveWebsite = async () => {
    setSavingSite(true);
    try {
      const { data } = await api.put(`/political-os/entities/${entity.id}/website`, { website: siteInput.trim() });
      setEntity(e => ({ ...e, website: data.website }));
      setSummary(data.verification_summary);
      setEditingSite(false);
      toast.success(data.website ? "Website updated — run the check to verify it" : "Website removed");
    } catch (e) { toast.error(errMsg(e, "Could not update the website")); }
    finally { setSavingSite(false); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.put(`/political-os/entities/${entity.id}/official-email`, { email: emailInput.trim() });
      setEntity(e => ({ ...e, official_email: data.official_email }));
      setSummary(data.verification_summary);
      setEditingEmail(false);
      setOtp("");
      await sendOtp();
    } catch (e) { toast.error(errMsg(e, "Could not update the email")); }
    finally { setSavingEmail(false); }
  };

  const requestReview = async () => {
    setRequestingReview(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/verification/request-review`);
      setReviewRequested(true);
      toast.success(data.already_done ? "Manual review is already complete"
        : "Review requested — our team typically completes it within 1 business day");
    } catch (e) { toast.error(errMsg(e, "Could not request the review")); }
    finally { setRequestingReview(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  if (!entity) return null;

  const name = entity.public_campaign_name || entity.legal_org_name;
  const comps = summary?.components || [];
  const emailDone = comps.find(c => c.key === "official_email")?.done;
  const siteDone = comps.find(c => c.key === "website_check")?.done;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="verify-campaign-page">
      <button onClick={() => navigate("/onboarding/checklist")} data-testid="verify-back-to-checklist"
        className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to launch checklist
      </button>
      <PageHeader
        eyebrow="Verification"
        title={`Verify ${name}`}
        subtitle="Donors see a verified badge on your checkout and microsites. Verify your official email (required) and website (recommended)."
      />

      <Card data-testid="verify-score-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-cyan-300" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Verification score</div>
              <div className="font-display text-2xl font-bold text-white" data-testid="verify-score-value">
                {summary?.score ?? 0}<span className="text-sm text-slate-500 font-normal">/100</span>
              </div>
            </div>
          </div>
          <div className="text-sm text-slate-400">{summary?.status}</div>
        </div>
        <div className="mt-4 grid gap-2">
          {comps.map(c => (
            <div key={c.key} className="flex items-center justify-between text-sm py-1.5" data-testid={`verify-component-${c.key}`}>
              <div className="flex items-center gap-2">
                {c.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4 text-slate-600" />}
                <span className={c.done ? "text-slate-300" : "text-slate-400"}>{c.label}</span>
              </div>
              <span className={`text-xs ${c.done ? "text-emerald-300" : "text-slate-600"}`}>+{c.points} pts</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Email verification */}
      <Card data-testid="verify-email-card" className={emailDone ? "border-emerald-400/40 bg-emerald-400/5" : ""}>
        <div className="flex items-start gap-3">
          <MailCheck className={`w-6 h-6 shrink-0 ${emailDone ? "text-emerald-300" : "text-cyan-300"}`} />
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-white">
              {emailDone ? "Official email verified" : "Verify your official campaign email"}
            </div>
            <div className="text-sm text-slate-400 mt-1">
              {emailDone
                ? <>Your official email <span className="text-slate-200">{entity.official_email}</span> is verified. Donors will see the verified badge.</>
                : <>We&apos;ll send a 6-digit code to <span className="text-slate-200 font-semibold">{entity.official_email || "your official email"}</span>. This is required to complete verification.</>}
            </div>

            {!emailDone && !otpSent && (
              <div className="mt-4">
                <Btn onClick={sendOtp} disabled={sending} data-testid="verify-send-otp-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                  Send verification code
                </Btn>
              </div>
            )}

            {!emailDone && otpSent && (
              <div className="mt-4 space-y-3">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-400/10 border border-amber-400/30" data-testid="verify-pending-banner">
                  <Inbox className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-100 leading-relaxed">
                    <span className="font-semibold">Verification pending — check your inbox.</span> A 6-digit code was sent to{" "}
                    <span className="font-semibold">{entity.official_email}</span>. It expires in 15 minutes. Check spam if you don&apos;t see it.
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code" maxLength={6} data-testid="verify-otp-input"
                    className="w-40 bg-slate-900/70 border border-white/10 rounded-lg px-3 py-2 text-center text-lg tracking-[0.4em] text-white outline-none focus:border-cyan-400/60" />
                  <Btn onClick={confirmOtp} disabled={otp.length !== 6 || confirming} data-testid="verify-confirm-otp-btn">
                    {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify
                  </Btn>
                  <button onClick={sendOtp} disabled={sending} data-testid="verify-resend-otp-btn"
                    className="text-xs text-slate-400 hover:text-cyan-300 underline underline-offset-4">
                    Resend code
                  </button>
                </div>
              </div>
            )}

            {!editingEmail ? (
              <button onClick={() => { setEmailInput(entity.official_email || ""); setEditingEmail(true); }}
                data-testid="verify-edit-email-btn"
                className="mt-3 block text-xs text-slate-400 hover:text-cyan-300 underline underline-offset-4">
                {entity.official_email ? "Change official email" : "Add official email"}
              </button>
            ) : (
              <div className="mt-4 space-y-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <input value={emailInput} onChange={e => setEmailInput(e.target.value)}
                    placeholder="team@yourcampaign.com" type="email" data-testid="verify-email-input"
                    className="flex-1 min-w-[220px] max-w-sm bg-slate-900/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60 placeholder-slate-500" />
                  <Btn onClick={saveEmail} disabled={savingEmail} data-testid="verify-save-email-btn">
                    {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save &amp; send code
                  </Btn>
                  <button onClick={() => setEditingEmail(false)} data-testid="verify-cancel-email-btn"
                    className="text-xs text-slate-400 hover:text-white">Cancel</button>
                </div>
                {emailDone && (
                  <div className="text-[11px] text-amber-300/80">
                    Changing the email resets its verification — we&apos;ll automatically send a new code to the new address.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Website verification */}
      <Card data-testid="verify-website-card" className={siteDone ? "border-emerald-400/40 bg-emerald-400/5" : ""}>
        <div className="flex items-start gap-3">
          <Globe className={`w-6 h-6 shrink-0 ${siteDone ? "text-emerald-300" : "text-cyan-300"}`} />
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-white">
              {siteDone ? "Website verified" : "Verify your campaign website"}
            </div>
            <div className="text-sm text-slate-400 mt-1">
              {entity.website
                ? <>We check <span className="text-slate-200">{entity.website}</span> for your campaign name or official email. Runs in seconds.</>
                : "No website on file for this campaign — add it below to earn +25 points, or skip it (email verification is what unlocks the checklist step)."}
            </div>
            {!editingSite ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {!siteDone && entity.website && (
                  <Btn onClick={checkWebsite} disabled={checkingSite} variant="secondary" data-testid="verify-website-btn">
                    {checkingSite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                    Check website now
                  </Btn>
                )}
                <button onClick={() => { setSiteInput(entity.website || ""); setEditingSite(true); }}
                  data-testid="verify-edit-website-btn"
                  className="text-xs text-slate-400 hover:text-cyan-300 underline underline-offset-4">
                  {entity.website ? "Change website" : "Add website"}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <input value={siteInput} onChange={e => setSiteInput(e.target.value)}
                    placeholder="https://yourcampaign.com" data-testid="verify-website-input"
                    className="flex-1 min-w-[220px] max-w-sm bg-slate-900/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60 placeholder-slate-500" />
                  <Btn onClick={saveWebsite} disabled={savingSite} data-testid="verify-save-website-btn">
                    {savingSite ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
                  </Btn>
                  <button onClick={() => setEditingSite(false)} data-testid="verify-cancel-website-btn"
                    className="text-xs text-slate-400 hover:text-white">Cancel</button>
                </div>
                {siteDone && (
                  <div className="text-[11px] text-amber-300/80">
                    Changing the website resets its verification — you&apos;ll need to run the check again.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Manual review */}
      <Card data-testid="verify-review-card"
        className={comps.find(c => c.key === "manual_review")?.done ? "border-emerald-400/40 bg-emerald-400/5" : ""}>
        <div className="flex items-start gap-3">
          <ShieldCheck className={`w-6 h-6 shrink-0 ${comps.find(c => c.key === "manual_review")?.done ? "text-emerald-300" : "text-cyan-300"}`} />
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-white">
              {comps.find(c => c.key === "manual_review")?.done ? "Manual review complete" : "TotalReach manual review"}
            </div>
            <div className="text-sm text-slate-400 mt-1" data-testid="verify-review-status">
              {comps.find(c => c.key === "manual_review")?.done
                ? "Our team confirmed your campaign — the final +10 points are yours."
                : reviewRequested
                  ? "Review requested — our team typically completes it within 1 business day. We'll notify you when it's done."
                  : "A human on our team confirms your campaign details for the final +10 points. Best requested after your email and website are verified."}
            </div>
            {!comps.find(c => c.key === "manual_review")?.done && !reviewRequested && (
              <div className="mt-4">
                <Btn variant="secondary" onClick={requestReview} disabled={requestingReview} data-testid="verify-request-review-btn">
                  {requestingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Request manual review
                </Btn>
              </div>
            )}
          </div>
        </div>
      </Card>

      {emailDone && (
        <div className="flex justify-end">
          <Btn onClick={() => navigate("/onboarding/checklist")} data-testid="verify-done-btn">
            Continue launch checklist <ArrowRight className="w-4 h-4" />
          </Btn>
        </div>
      )}
    </div>
  );
}
